import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";
import type {
  Upload, Run, RunStatusOut, DecisionRecord, ChatMessage,
  ValidationReport, CheckAction, IngestionJob,
  RegistryStats, RegistryDataset, RegistryDonor, RegistrySample,
  RegistryQueryResult, DiagnosticsReport,
  AnalysisMeta, RunMetadata, PlotOut, DegIn, DegOut,
  SubclusterIn, SubclusterOut,
  GseaIn, GseaOut,
  CrossProbeOut, CrossDegIn, CrossDegOut,
  MetaRunIn, MetaResultOut,
  DonorMapIn, DonorMapOut,
  RefMapIn, RefMapOut,
  CatalogDataset, CatalogGseCheckResult,
  CatalogDatasetPatch, CatalogDonorPatch, CatalogSamplePatch,
  Me, UploadPreview, ConfirmIngestionBody, ExtraFileContent,
  ProposedPlan, RawArchiveConfirm,
} from "./types";

// -----------------------------------------------------------------------------
// Auth token plumbing
//
// The frontend supports two auth modes that share the same axios client:
//   - Supabase JWT (default): AuthProvider calls setAccessTokenGetter() with
//     a closure over the live session, so every request reads the *current*
//     token (handles refresh transparently).
//   - API key (CLI / legacy): kept in localStorage. The Settings panel for it
//     was removed in Phase 5/4c, but the storage key still works if someone
//     sets it manually (e.g. for image / WS access during transition).
//
// The interceptor prefers the JWT when both are present.
// -----------------------------------------------------------------------------

const API_KEY_STORAGE = "pbmc.apiKey";

export function getStoredApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE) || "";
}

export function setStoredApiKey(key: string): void {
  localStorage.setItem(API_KEY_STORAGE, key);
}

type TokenGetter = () => string | null;
let _accessTokenGetter: TokenGetter = () => null;

/** Called once by AuthProvider so the interceptor can read the live session token. */
export function setAccessTokenGetter(fn: TokenGetter): void {
  _accessTokenGetter = fn;
}

function getAccessToken(): string | null {
  return _accessTokenGetter();
}

/** Header object for raw ``fetch`` calls (SSE / streaming) that don't go
 *  through the axios interceptor. Mirrors the same JWT-preferred,
 *  API-key-fallback logic. */
export function authHeader(): Record<string, string> {
  const tok = getAccessToken();
  if (tok) return { Authorization: `Bearer ${tok}` };
  const k = getStoredApiKey();
  if (k) return { "X-API-Key": k };
  return {};
}

// Axios doesn't have a stable "extra config" surface, so we attach
// per-request opt-outs via a small augmented config type. Anything that
// wants to do its own error UI sets ``skipErrorToast: true`` on the
// per-call axios options.
declare module "axios" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AxiosRequestConfig {
    /** If true, the global error interceptor will NOT pop a toast for
     *  this request. Component still gets the rejected promise to
     *  display the error inline however it wants. */
    skipErrorToast?: boolean;
  }
}

/** Extract the most informative human-readable message from an axios
 *  error. Prefers FastAPI's structured ``detail`` field. */
function extractErrorMessage(error: AxiosError): string {
  const data: any = error.response?.data;
  // FastAPI: {"detail": "..."} or {"detail": [{loc, msg, type}, ...]}
  if (data) {
    if (typeof data === "string") return data;
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail) && data.detail.length) {
      const first = data.detail[0];
      if (typeof first === "string") return first;
      if (first && typeof first.msg === "string") return first.msg;
    }
    if (typeof data.message === "string") return data.message;
  }
  return error.response?.statusText
      || error.message
      || "Request failed";
}

function makeClient(): AxiosInstance {
  const c = axios.create({
    baseURL: "/api",
    timeout: 60_000,
  });
  c.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      cfg.headers["Authorization"] = `Bearer ${token}`;
      return cfg;
    }
    // Fallback to API key (only useful before login, or for tools that
    // still rely on it). Backend's current_user accepts either.
    const k = getStoredApiKey();
    if (k) {
      cfg.headers["X-API-Key"] = k;
    }
    return cfg;
  });
  c.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const cfg: any = error.config ?? {};
      // Always re-reject so per-call .catch() / TanStack Query's
      // error state still fire. The toast is purely additive UX.
      if (cfg.skipErrorToast) {
        return Promise.reject(error);
      }
      const status = error.response?.status;
      const method = (error.config?.method ?? "").toLowerCase();
      // 401 -- the AuthProvider will redirect to /login on next render;
      // popping a toast here just adds noise during the redirect.
      if (status === 401) {
        return Promise.reject(error);
      }
      // 404 on a GET is almost always "the thing was deleted while a
      // background poller was in flight" (delete cascade → next /run/X
      // poll → 404). The UI's empty-state / redirect already handles
      // it; an extra toast is just noise and looks like a real bug to
      // the user. Keep 404 toasts for write methods (POST/PATCH/DELETE)
      // -- those ARE user-initiated and a 404 there means a real
      // problem worth shouting about.
      if (status === 404 && method === "get") {
        return Promise.reject(error);
      }
      // Background polling (TanStack Query refetches every few seconds
      // for run status / decisions / figures) is rate-limited so we
      // don't spam toasts when the backend is briefly down.
      const detail = extractErrorMessage(error);
      const prefix = status ? `${status} · ` : "";
      const msg = `${prefix}${detail}`;
      // Use a stable id derived from method+url+message so background
      // pollers (TanStack Query refetches /runs/{id}/status every few
      // seconds; if the backend is briefly down that would otherwise
      // stack 10+ identical toasts) collapse to ONE persistent toast
      // that just keeps refreshing.
      const id = `${error.config?.method ?? ""}:${error.config?.url ?? ""}:${msg}`;
      toast.error(msg, { id });
      return Promise.reject(error);
    }
  );
  return c;
}

const http = makeClient();
export default http;

// ---------- Health ----------
export async function getHealth(): Promise<{ status: string; auth_mode: string; anthropic_configured: boolean }> {
  const r = await http.get("/health");
  return r.data;
}

export async function getDiagnostics(): Promise<DiagnosticsReport> {
  const r = await http.get<DiagnosticsReport>("/health/diagnostics");
  return r.data;
}

/** Returns the signed-in user + admin status. AuthProvider calls this
 *  on session change so the rest of the app can gate admin-only UI on
 *  it. 401 is normal pre-login and is surfaced as a rejection. */
export async function getMe(): Promise<Me> {
  const r = await http.get<Me>("/me", { skipErrorToast: true });
  return r.data;
}

// ---------- Uploads (renamed from Datasets) ----------
/**
 * List the current user's uploads. The optional `asCatalog` filter
 * splits the workspace:
 *   - undefined  -> legacy, no filter (used by tests / scripts)
 *   - false      -> personal Uploads page (excludes catalog uploads)
 *   - true       -> admin Catalog Uploads page (only catalog uploads)
 */
export async function listUploads(
  opts: { asCatalog?: boolean } = {},
): Promise<Upload[]> {
  const r = await http.get<Upload[]>("/uploads", {
    params: opts.asCatalog === undefined ? {} : { as_catalog: opts.asCatalog },
  });
  return r.data;
}

export async function uploadFile(args: {
  name: string;
  kind: string;
  sampleMetadata?: Record<string, unknown>;
  files: File[];
  // Admin-only (Phase 6). When true, the ingestion bridge writes
  // owner_user_id=NULL so the resulting dataset appears in the public
  // Catalog. Non-admins get a 403 from the backend.
  asCatalog?: boolean;
  // Phase 7. When true (and kind=geo_bundle), the ingestion bridge
  // pauses after extracting the archive so the user can confirm
  // per-sample / per-donor metadata via the Review page.
  needsReview?: boolean;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<Upload> {
  if (!args.files || args.files.length === 0) {
    throw new Error("at least one file is required");
  }
  const fd = new FormData();
  fd.append("name", args.name);
  fd.append("kind", args.kind);
  if (args.sampleMetadata) {
    fd.append("sample_metadata", JSON.stringify(args.sampleMetadata));
  }
  // FastAPI receives `files: List[UploadFile]` when the same field name
  // appears multiple times in multipart/form-data.
  for (const f of args.files) {
    fd.append("files", f, f.name);
  }
  const totalSize = args.files.reduce((s, f) => s + f.size, 0);
  const params: Record<string, string> = {};
  if (args.asCatalog)   params.as_catalog   = "true";
  if (args.needsReview) params.needs_review = "true";
  const r = await http.post<Upload>("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    params: Object.keys(params).length ? params : undefined,
    timeout: 0,
    onUploadProgress: (e) => {
      if (args.onProgress) {
        args.onProgress(e.loaded, e.total ?? totalSize);
      }
    },
  });
  return r.data;
}

export async function deleteUpload(
  id: string,
  opts: { purgeRegistry?: boolean } = {},
): Promise<void> {
  await http.delete(`/uploads/${id}`, {
    params: { purge_registry: opts.purgeRegistry ? "true" : "false" },
  });
}

/**
 * Rename an Upload's display label.
 *
 * Replaces the old `updateUploadMetadata`: editing
 * Upload.sample_metadata post-hoc looked useful in the UI but did
 * nothing -- curate reads it once at ingestion then never again. For
 * metadata fixes after ingestion the user edits donor/sample rows in
 * the Registry / Catalog. Rename is the one Upload field people
 * genuinely want to change (typos).
 */
export async function renameUpload(id: string, name: string): Promise<Upload> {
  const r = await http.patch<Upload>(`/uploads/${id}`, { name });
  return r.data;
}

// ---------- Ingestion bridge (Phase 2) ----------
//
// Returns the latest IngestionJob for an upload, or null if no job has
// ever been enqueued (the backend returns 404 in that case; we
// translate it to null so React Query can render a "not started"
// state cleanly).
export async function getLatestIngestion(uploadId: string): Promise<IngestionJob | null> {
  try {
    const r = await http.get<IngestionJob>(`/uploads/${uploadId}/ingestion`);
    return r.data;
  } catch (e: any) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

export async function retryIngestion(uploadId: string): Promise<IngestionJob> {
  const r = await http.post<IngestionJob>(`/uploads/${uploadId}/ingestion/retry`);
  return r.data;
}

// ---------- Pre-curate review (Phase 7) ----------

/** Fetch the post-extract preview for a needs_review upload. The Review
 *  page polls this until status === "pending_review" and samples is
 *  non-empty, then renders the editable form. */
export async function getUploadPreview(uploadId: string): Promise<UploadPreview> {
  const r = await http.get<UploadPreview>(`/uploads/${uploadId}/preview`);
  return r.data;
}

/** Submit user-confirmed metadata; backend writes the YAML directly
 *  from this and resumes ingestion into curate / register / index. */
export async function confirmUploadIngestion(
  uploadId: string, body: ConfirmIngestionBody,
): Promise<IngestionJob> {
  const r = await http.post<IngestionJob>(
    `/uploads/${uploadId}/confirm-ingestion`, body,
  );
  return r.data;
}

// ---------- Raw-archive structure review ----------
//
// The propose-and-confirm path for kind=raw_archive uploads. After the
// upload extracts + parks at pending_review, the worker stashes a
// structure-interpreter plan; the Review page fetches it, the user
// confirms / overrides the loader, and confirmRawArchive resumes curate.

/** Fetch the structure interpreter's proposed integration plan for a
 *  raw_archive upload parked at pending_review.
 *
 *  Throws on:
 *   - 404 — upload missing / not owned / no ingestion job yet
 *   - 409 — a job exists but no plan yet (still extracting). The thrown
 *     axios error carries response.status so the caller can poll.
 *
 *  We pass skipErrorToast so the Review page (which polls while the
 *  archive is still extracting) doesn't pop a red toast on every 409. */
export async function getProposedPlan(uploadId: string): Promise<ProposedPlan> {
  const r = await http.get<ProposedPlan>(
    `/uploads/${uploadId}/proposed-plan`,
    { skipErrorToast: true } as any,
  );
  return r.data;
}

/** Confirm (or override) the proposed loader for a raw_archive upload
 *  and resume ingestion into curate. Body shape differs from the
 *  geo_bundle confirm (see confirmUploadIngestion); the backend
 *  dispatches on the upload's kind. Omit `loader` to accept the plan's
 *  suggested_loader. */
export async function confirmRawArchive(
  uploadId: string, body: RawArchiveConfirm,
): Promise<IngestionJob> {
  const r = await http.post<IngestionJob>(
    `/uploads/${uploadId}/confirm-ingestion`, body,
  );
  return r.data;
}

/** Fetch the text content of a peek-able supplementary file in the
 *  extracted upload. `relPath` round-trips from UploadPreview.extra_files
 *  / samples[].extra_files; the backend validates containment +
 *  extension allowlist + caps the read at 256 KB. */
export async function getUploadExtraFile(
  uploadId: string, relPath: string,
): Promise<ExtraFileContent> {
  const r = await http.get<ExtraFileContent>(
    `/uploads/${uploadId}/extra-file`,
    { params: { path: relPath } },
  );
  return r.data;
}

export function ingestionWsUrl(uploadId: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host;
  const qs = authQueryString();
  return `${proto}//${host}/api/uploads/${uploadId}/ingestion/ws${qs ? "?" + qs : ""}`;
}

// ---------- Registry browser (Phase 2.4) ----------
export async function getRegistryStats(): Promise<RegistryStats> {
  const r = await http.get<RegistryStats>("/registry/stats");
  return r.data;
}

export async function listRegistryDatasets(): Promise<RegistryDataset[]> {
  const r = await http.get<RegistryDataset[]>("/registry/datasets");
  return r.data;
}

export async function listRegistryDonors(datasetKey: string): Promise<RegistryDonor[]> {
  const r = await http.get<RegistryDonor[]>(`/registry/datasets/${encodeURIComponent(datasetKey)}/donors`);
  return r.data;
}

export async function listRegistrySamples(datasetKey: string): Promise<RegistrySample[]> {
  const r = await http.get<RegistrySample[]>(`/registry/datasets/${encodeURIComponent(datasetKey)}/samples`);
  return r.data;
}

// listRegistryCells retired in Step 3 along with the cells_index table.
// For cell-level inspection of a dataset use /runs/{id}/analysis/meta on
// its reference run (gives per-cell counts + gene list + cell types)
// or LibraryViewerPage's metadata browser.

export async function runRegistryQuery(
  sql: string,
  limit: number = 500,
): Promise<RegistryQueryResult> {
  const r = await http.post<RegistryQueryResult>("/registry/query", { sql, limit });
  return r.data;
}

// ---------- Catalog (Phase 6) ----------
//
// Public catalog of admin-curated datasets. Read-only from the user's
// perspective (membership is admin-only); they can start a Run on any
// catalog entry without re-uploading the source data.

export async function listCatalogDatasets(opts: {
  q?: string;
  source?: string;
  species?: string;
  // Phase 3 filters: case-insensitive EXISTS match against donors.
  // disease / disease_subtype come from the breakdown chips the
  // backend computes per dataset.
  disease?: string;
  disease_subtype?: string;
  // Phase 4.1 -- admin only. When true, returns soft-deleted entries
  // (used by the Trash page). Backend silently ignores for non-admin.
  includeDeleted?: boolean;
} = {}): Promise<CatalogDataset[]> {
  const r = await http.get<CatalogDataset[]>("/catalog/datasets", {
    params: {
      q: opts.q || undefined,
      source: opts.source || undefined,
      species: opts.species || undefined,
      disease: opts.disease || undefined,
      disease_subtype: opts.disease_subtype || undefined,
      include_deleted: opts.includeDeleted ? "true" : undefined,
    },
  });
  return r.data;
}

export async function getCatalogDataset(datasetKey: string): Promise<CatalogDataset> {
  const r = await http.get<CatalogDataset>(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}`,
  );
  return r.data;
}

/** Advisory call used by the upload form: returns a GSE id parsed from
 *  `name` (or null if nothing looked GSE-shaped) plus any catalog hits.
 *  Never throws on a missing registry -- a 503 surfaces as an empty
 *  hits list so the form isn't blocked when the catalog is offline. */
export async function checkCatalogGse(name: string): Promise<CatalogGseCheckResult> {
  try {
    const r = await http.get<CatalogGseCheckResult>("/catalog/check-gse", {
      params: { name },
      // The form polls this on every keystroke (debounced); a transient
      // backend hiccup shouldn't trigger a red error toast.
      skipErrorToast: true,
    });
    return r.data;
  } catch {
    return { gse_id: null, hits: [] };
  }
}

// ---------- Catalog admin (Phase 6) ----------
//
// All mutations gated server-side on user.is_admin (HTTP 403 otherwise).
// Frontend additionally hides the buttons via useAdmin() so non-admins
// don't see a button that would just 403.

/** SOFT delete (Phase 4.1): sets deleted_at, hides everywhere; admin
 *  can restore for 30 days before the cleanup script hard-purges. */
export async function deleteCatalogDataset(datasetKey: string): Promise<void> {
  await http.delete(`/catalog/datasets/${encodeURIComponent(datasetKey)}`);
}

/** Undo a previous soft-delete. Cascade-restores the same uploads +
 *  runs that were soft-deleted by the original delete event. */
export async function restoreCatalogDataset(datasetKey: string): Promise<void> {
  await http.post(`/catalog/datasets/${encodeURIComponent(datasetKey)}/restore`);
}

/** HARD delete (Phase 4.1): irreversibly wipes a soft-deleted dataset.
 *  409s on a live dataset -- must soft-delete first. Used by the
 *  Trash page "Purge now" button and by the 30-day cleanup script. */
export async function purgeCatalogDataset(datasetKey: string): Promise<void> {
  await http.delete(`/catalog/datasets/${encodeURIComponent(datasetKey)}/purge`);
}

export async function patchCatalogDataset(
  datasetKey: string, body: CatalogDatasetPatch,
): Promise<CatalogDataset> {
  const r = await http.patch<CatalogDataset>(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}`, body,
  );
  return r.data;
}

/** Re-run curate for a catalog dataset (admin only). Regenerates
 *  data/curated/<key>.h5ad from the current registry state, so
 *  metadata edits made via patchCatalogDonor / patchCatalogSample
 *  (age_group / disease_subtype / timepoint / etc.) flow into the
 *  broadcast obs columns. Existing reference runs' 08_final.h5ad
 *  are NOT touched -- the response's next_step_hint tells the user
 *  whether to also Rebuild reference. Subprocess; can take 10-90 s
 *  depending on dataset size, so caller should show a spinner. */
export async function recurateCatalogDataset(
  datasetKey: string,
): Promise<{
  dataset_key: string;
  elapsed_sec: number;
  size_before: number;
  size_after: number;
  stdout_tail: string;
  next_step_hint: string;
}> {
  const r = await http.post(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}/recurate`,
    {},
    { timeout: 1_000_000 },  // 16 min, matches backend 900s timeout
  );
  return r.data;
}

/** One-click: pull metadata from GEO SOFT into datasets.description_json.
 *  Returns the refreshed dataset row. Admin only (backend gates). */
export async function fetchGeoDescription(
  datasetKey: string,
): Promise<CatalogDataset> {
  const r = await http.post<CatalogDataset>(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}/fetch-geo`,
    null,
    // GEO can take a few seconds; bump the axios timeout above default.
    { timeout: 60_000 },
  );
  return r.data;
}

/** Re-run input-matrix sniffing on the curated h5ad and persist the
 *  resulting x_layer_kind on the registry row (admin only).
 *
 *  Use cases:
 *   - Backfill legacy rows (x_layer_kind = null because curated before
 *     the column existed).
 *   - Recover from a misclassification (rare, but the escape hatch the
 *     rigor-conscious admin wants).
 *
 *  Reads + re-writes the h5ad (to stamp adata.uns['x_layer_kind']) so
 *  it can take a few seconds on large datasets; bump the timeout. */
export async function redetectXLayerKind(
  datasetKey: string,
): Promise<CatalogDataset> {
  const r = await http.post<CatalogDataset>(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}/redetect-x-layer-kind`,
    null,
    { timeout: 120_000 },
  );
  return r.data;
}

/** Spawn the catalog dataset's reference Run (admin only).
 *  ``force=true`` demotes any existing reference + spawns fresh. */
export async function buildCatalogReference(
  datasetKey: string, force = false,
): Promise<{ run_id: string; status: string; rebuilt: boolean }> {
  const r = await http.post(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}/build-reference`,
    null,
    { params: force ? { force: "true" } : undefined },
  );
  return r.data;
}

/** Admin-only (B7): download a controlled cohort export (.zip) — cohort.h5ad
 *  + a provenance/billing manifest. Streamed via XHR (blob) so auth stays
 *  header-only; the server records a `dataset.export` audit-ledger row. */
export async function exportCohort(
  datasetKey: string,
  opts?: { celltypeCol?: string; celltypeValues?: string[]; disease?: string[] },
): Promise<void> {
  const params: Record<string, string | string[]> = {};
  if (opts?.celltypeCol) params.celltype_col = opts.celltypeCol;
  if (opts?.celltypeValues?.length) params.celltype_values = opts.celltypeValues;
  if (opts?.disease?.length) params.disease = opts.disease;
  const r = await http.get(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}/export`,
    { params, responseType: "blob", paramsSerializer: { indexes: null } },
  );
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${datasetKey}_export.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function patchCatalogDonor(
  datasetKey: string, donorKey: string, body: CatalogDonorPatch,
): Promise<{ donor: Record<string, unknown>; cells_index_updated: number }> {
  const r = await http.patch(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}`
    + `/donors/${encodeURIComponent(donorKey)}`, body,
  );
  return r.data;
}

export async function patchCatalogSample(
  datasetKey: string, sampleKey: string, body: CatalogSamplePatch,
): Promise<{ sample: Record<string, unknown>; cells_index_updated: number }> {
  const r = await http.patch(
    `/catalog/datasets/${encodeURIComponent(datasetKey)}`
    + `/samples/${encodeURIComponent(sampleKey)}`, body,
  );
  return r.data;
}

// ---------- Runs ----------
/**
 * List runs visible to the caller. `isCatalogReference` toggles:
 *   - undefined -> legacy, owner_id-only filter
 *   - false     -> personal Runs page (excludes catalog reference runs)
 *   - true      -> admin Catalog Runs page (all is_catalog_reference=true
 *                   runs across owners; backend enforces admin auth via
 *                   the user_id lookup so non-admin requests of this
 *                   flavour only return their own anyway)
 */
export async function listRuns(
  opts: { isCatalogReference?: boolean } = {},
): Promise<Run[]> {
  const r = await http.get<Run[]>("/runs", {
    params: opts.isCatalogReference === undefined
      ? {}
      : { is_catalog_reference: opts.isCatalogReference },
  });
  return r.data;
}

export async function getRun(id: string): Promise<Run> {
  const r = await http.get<Run>(`/runs/${id}`);
  return r.data;
}

export async function createRun(body: {
  // Provide exactly one input source. Backend rejects both / neither
  // with a 400 -- we keep the type permissive so callers can pick.
  upload_id?: string;
  from_dataset_key?: string;
  name?: string;
  config_overrides?: Record<string, unknown>;
  annotation_model?: string;
  // Admin-only -- marks this run as the canonical catalog reference
  // for `from_dataset_key`. Requires from_dataset_key + admin.
  is_catalog_reference?: boolean;
  // Admin-only, requires is_catalog_reference=true. When set, demotes
  // any alive reference for the same dataset before creating the new
  // one (Rebuild button: one-click overwrite). In-flight runs get
  // canceled; succeeded runs are kept on disk but lose the reference
  // flag. Without this the backend 409s on the 1:1 conflict.
  replace_existing?: boolean;
}): Promise<Run> {
  const r = await http.post<Run>("/runs", body);
  return r.data;
}

export async function getRunStatus(id: string, logTail = 200): Promise<RunStatusOut> {
  const r = await http.get<RunStatusOut>(`/runs/${id}/status`, {
    params: { log_tail: logTail },
  });
  return r.data;
}

export async function getRunDecisions(id: string): Promise<DecisionRecord[]> {
  const r = await http.get<DecisionRecord[]>(`/runs/${id}/decisions`);
  return r.data;
}

export async function getRunSummary(id: string): Promise<{ summary_md: string | null; decisions_md: string | null }> {
  const r = await http.get(`/runs/${id}/summary`);
  return r.data;
}

export async function getRunFigures(id: string): Promise<string[]> {
  const r = await http.get<string[]>(`/runs/${id}/figures`);
  return r.data;
}

// DELETE one figure file from a run's figures/ dir. Backend gates:
//   - catalog reference run -> admin only (returns 403 otherwise)
//   - other runs -> owner or admin
// Caller should optimistically invalidate ["run-figures", runId] after.
export async function deleteRunFigure(runId: string, name: string): Promise<void> {
  // Encode each path segment but keep "/" between them so subfolder
  // figures (e.g. queries/<qid>/figures/...) round-trip correctly.
  const encoded = name.split("/").map(encodeURIComponent).join("/");
  await http.delete(`/files/runs/${runId}/figures/${encoded}`);
}

// ---------- Custom analyses (violin / heatmap / DEG / volcano / GSEA) ----------

export async function getAnalysisMeta(
  id: string, opts: { q?: string } = {},
): Promise<AnalysisMeta> {
  const r = await http.get<AnalysisMeta>(`/runs/${id}/analysis/meta`, {
    params: opts.q ? { q: opts.q } : undefined,
  });
  return r.data;
}

/**
 * Full obs/var browser for the Metadata tab on RunDetailPage. Reads
 * the run's 08_final.h5ad and returns column summaries + per-donor
 * breakdown.
 */
export async function getRunMetadata(id: string): Promise<RunMetadata> {
  const r = await http.get<RunMetadata>(`/runs/${id}/analysis/metadata`);
  return r.data;
}

/**
 * Same shape as getRunMetadata but reads the CURATED h5ad
 * (data/curated/<key>.h5ad) -- available right after ingestion
 * finishes, before any pipeline Run. Useful for verifying batch /
 * disease_subtype / donor mappings BEFORE wasting compute on a
 * full pipeline run.
 */
export async function getDatasetMetadata(datasetKey: string): Promise<RunMetadata> {
  const r = await http.get<RunMetadata>(
    `/datasets/${encodeURIComponent(datasetKey)}/metadata`,
  );
  return r.data;
}

/**
 * Update donor metadata on ANY dataset (catalog OR user-owned).
 * Auth handled server-side -- catalog datasets are admin-only,
 * private datasets are owner-or-admin. Cascades into cells_index.
 *
 * Same body shape as patchCatalogDonor; different URL so private
 * dataset owners can call it without needing admin privileges.
 */
export async function patchDatasetDonor(
  datasetKey: string, donorKey: string, body: CatalogDonorPatch,
): Promise<{ donor: RegistryDonor; cells_index_updated: number }> {
  const r = await http.patch(
    `/datasets/${encodeURIComponent(datasetKey)}/donors/${encodeURIComponent(donorKey)}`,
    body,
  );
  return r.data;
}

/**
 * Same as patchDatasetDonor but for sample-level edits.
 */
export async function patchDatasetSample(
  datasetKey: string, sampleKey: string, body: CatalogSamplePatch,
): Promise<{ sample: RegistrySample; cells_index_updated: number }> {
  const r = await http.patch(
    `/datasets/${encodeURIComponent(datasetKey)}/samples/${encodeURIComponent(sampleKey)}`,
    body,
  );
  return r.data;
}

export async function plotViolin(
  id: string, body: { gene: string; groupby: string },
): Promise<PlotOut> {
  const r = await http.post<PlotOut>(`/runs/${id}/analysis/violin`, body);
  return r.data;
}

export async function plotHeatmap(
  id: string, body: { genes: string[]; groupby: string; use_dotplot?: boolean },
): Promise<PlotOut> {
  const r = await http.post<PlotOut>(`/runs/${id}/analysis/heatmap`, body);
  return r.data;
}

export async function runDeg(id: string, body: DegIn): Promise<DegOut> {
  // wilcoxon on ~3k cells is fast; on 100k+ it can take a minute. Bump the
  // axios timeout for this call only.
  const r = await http.post<DegOut>(`/runs/${id}/analysis/deg`, body, {
    timeout: 300_000,
  });
  return r.data;
}

// ----- Cross-dataset analysis (Phase 6.1) -----

export async function probeCrossDatasets(
  datasetKeys: string[],
): Promise<CrossProbeOut> {
  const r = await http.get<CrossProbeOut>("/cross_analysis/probe", {
    params: { dataset_keys: datasetKeys.join(",") },
  });
  return r.data;
}

export async function runCrossDeg(body: CrossDegIn): Promise<CrossDegOut> {
  // Each dataset's h5ad load is 1-3 s; concat + Wilcoxon on 50k+ cells
  // can be 30-90 s. Generous timeout matches per-run /deg. Backend now
  // runs this off the event loop via asyncio.to_thread so other API
  // requests (catalog list, runs list, etc.) keep working during the
  // long DE -- otherwise axios's 60 s timeout fires elsewhere.
  const r = await http.post<CrossDegOut>("/cross_analysis/de", body, {
    timeout: 600_000,
  });
  return r.data;
}

// ----- Cross-cohort meta-analysis (Phase 5.3) -----

export async function runMetaAnalysis(body: MetaRunIn): Promise<MetaResultOut> {
  // pseudobulk-per-donor + DerSimonian-Laird across cohorts; like cross-DE
  // this can read several h5ads and take 30-120 s, so bump the timeout.
  // Backend dispatches via asyncio.to_thread so other requests keep working.
  //
  // Send adjust_covariates explicitly (default [] = today's unadjusted
  // behaviour). Each value must be an obs column != groupby; backend 422s
  // otherwise. We don't pre-validate here -- the form gates that.
  const r = await http.post<MetaResultOut>("/meta_analysis/run", {
    ...body,
    adjust_covariates: body.adjust_covariates ?? [],
  }, {
    timeout: 600_000,
  });
  return r.data;
}

export async function getMetaResult(queryHash: string): Promise<MetaResultOut> {
  const r = await http.get<MetaResultOut>(
    `/meta_analysis/result/${encodeURIComponent(queryHash)}`,
    { skipErrorToast: true } as any,
  );
  return r.data;
}

// ----- Donor map (donor-level pseudobulk 3D embedding) -----

export async function runDonorMap(body: DonorMapIn): Promise<DonorMapOut> {
  // Reads each cohort's h5ad, pseudobulks per donor + PCA-projects to 3D.
  // Same I/O cost profile as meta-analysis (several h5ads, 30-120 s), so
  // bump the axios timeout well past the 60 s default.
  const r = await http.post<DonorMapOut>("/donor_map/run", body, {
    timeout: 600_000,
  });
  return r.data;
}

export async function getDonorMapResult(queryHash: string): Promise<DonorMapOut> {
  const r = await http.get<DonorMapOut>(
    `/donor_map/result/${encodeURIComponent(queryHash)}`,
    { skipErrorToast: true } as any,
  );
  return r.data;
}

// ----- Reference mapping ("position MY sample against the catalog") -----

export async function runReferenceMapping(body: RefMapIn): Promise<RefMapOut> {
  // Reads the query h5ad (a user run via query_run_id OR a catalog cohort via
  // query_dataset_key) + each reference cohort's h5ad, pseudobulks per donor,
  // and positions the query on the catalog's healthy<->disease axis. Same I/O
  // cost profile as donor map (several h5ads, 30-120 s), so bump the axios
  // timeout well past the 60 s default.
  const r = await http.post<RefMapOut>("/reference_mapping/run", body, {
    timeout: 600_000,
  });
  return r.data;
}

export async function getReferenceMappingResult(queryHash: string): Promise<RefMapOut> {
  const r = await http.get<RefMapOut>(
    `/reference_mapping/result/${encodeURIComponent(queryHash)}`,
    { skipErrorToast: true } as any,
  );
  return r.data;
}

export async function getCrossDegResult(
  queryHash: string,
): Promise<CrossDegOut> {
  // Restore a cached cross-dataset DE result by its query hash.
  // 404 means the user's localStorage references a result that was
  // pruned (we currently never prune, but defensive).
  const r = await http.get<CrossDegOut>(
    `/cross_analysis/result/${encodeURIComponent(queryHash)}`,
    // Probe call -- don't toast the error if the cached result is gone;
    // the page will silently start fresh.
    { skipErrorToast: true } as any,
  );
  return r.data;
}

export async function runSubcluster(
  id: string, body: SubclusterIn,
): Promise<SubclusterOut> {
  const r = await http.post<SubclusterOut>(
    `/runs/${id}/analysis/subcluster`, body,
  );
  return r.data;
}

export async function runGsea(id: string, body: GseaIn): Promise<GseaOut> {
  // Enrichr can take 5-30s for multiple libraries. Bump axios timeout.
  const r = await http.post<GseaOut>(`/runs/${id}/analysis/gsea`, body, {
    timeout: 180_000,
  });
  return r.data;
}

export async function deleteRun(id: string): Promise<void> {
  await http.delete(`/runs/${id}`);
}

/**
 * Hard-cancel a queued / running / needs_input pipeline:
 *   - kills the pipeline subprocess (SIGTERM, then SIGKILL after 5s)
 *   - flips Run.status -> canceled
 *   - removes the run's output_dir on disk (no recovery)
 *
 * Returns the refreshed Run row so the caller can update UI.
 */
export async function cancelRun(id: string): Promise<Run> {
  const r = await http.post<Run>(`/runs/${id}/cancel`);
  return r.data;
}

export async function getValidationReport(id: string): Promise<ValidationReport> {
  const r = await http.get<ValidationReport>(`/runs/${id}/validation`);
  return r.data;
}

export async function resumeRun(
  id: string,
  overrides: Record<string, CheckAction>,
): Promise<Run> {
  const r = await http.post<Run>(`/runs/${id}/resume`, { overrides });
  return r.data;
}

// ---------- Chat / messages ----------
export async function listMessages(runId: string): Promise<ChatMessage[]> {
  const r = await http.get<ChatMessage[]>(`/runs/${runId}/messages`);
  return r.data;
}

// ---------- URLs ----------
//
// `<img src>` / `<a href>` / WebSocket handshakes can't attach custom
// headers, so static-file and WS URLs ride credentials as a query
// string. Two flavors:
//   - ``access_token=<jwt>`` for Supabase auth (preferred)
//   - ``api_key=<key>`` for legacy / CLI API-key auth (fallback)
// Backend ``current_user`` accepts either.

/** Build the auth query string for header-less requests. Empty if neither
 *  a JWT nor an API key is available. */
function authQueryString(): string {
  const tok = getAccessToken();
  if (tok) return `access_token=${encodeURIComponent(tok)}`;
  const key = getStoredApiKey();
  if (key) return `api_key=${encodeURIComponent(key)}`;
  return "";
}

export function withKey(path?: string | null): string {
  // Null-safe: a missing/undefined URL must never throw (it used to call
  // .includes on undefined and take down the whole Chat render).
  if (!path) return "";
  const qs = authQueryString();
  if (!qs) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}${qs}`;
}

export function figureUrl(runId: string, name: string): string {
  return withKey(`/api/files/runs/${runId}/figures/${encodeURIComponent(name)}`);
}

export function queryFigureUrl(runId: string, queryId: string, name: string): string {
  return withKey(
    `/api/files/runs/${runId}/queries/${queryId}/figures/${encodeURIComponent(name)}`
  );
}

export function reportUrl(runId: string, name: string): string {
  return withKey(`/api/files/runs/${runId}/reports/${encodeURIComponent(name)}`);
}

export function checkpointUrl(runId: string, name: string): string {
  return withKey(`/api/files/runs/${runId}/checkpoints/${encodeURIComponent(name)}`);
}

export function wsUrl(runId: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host;
  const qs = authQueryString();
  return `${proto}//${host}/api/runs/${runId}/ws${qs ? "?" + qs : ""}`;
}

// ---------------------------------------------------------------------------
// System info -- backend version, package versions, annotation models.
// Powers /system page so users can cite versions in methods sections and
// admins can spot dep drift over time.
// ---------------------------------------------------------------------------

import type { SystemInfo, RunEnvironment, BackupStatus } from "./types";

export async function getSystemInfo(): Promise<SystemInfo> {
  const r = await http.get<SystemInfo>("/system/info");
  return r.data;
}

/** Last-backup info read from data/backups/last_run.json. Surfaced on
 *  the System Info page so admin can spot a stale or never-ran backup
 *  without having to RDP into the box and inspect Task Scheduler. */
export async function getBackupStatus(): Promise<BackupStatus> {
  const r = await http.get<BackupStatus>("/system/backup-status");
  return r.data;
}

/** Aggregated Run reliability KPIs for the System Info dashboard.
 *  Non-admin viewers see their own runs only; admins see all. */
export async function getRunStats(): Promise<import("./types").RunStats> {
  const r = await http.get<import("./types").RunStats>("/system/run-stats");
  return r.data;
}

// ---------------------------------------------------------------------------
// Audit log (admin only). Lists destructive / admin events with optional
// filters. Used by the Admin → Audit log page.
// ---------------------------------------------------------------------------

import type { AuditEvent } from "./types";

export async function listAuditEvents(opts: {
  limit?: number;
  action?: string;       // exact match, OR "catalog." for prefix
  targetId?: string;
  actorUserId?: string;
} = {}): Promise<AuditEvent[]> {
  const params: Record<string, string | number> = {};
  if (opts.limit !== undefined)       params.limit = opts.limit;
  if (opts.action !== undefined)      params.action = opts.action;
  if (opts.targetId !== undefined)    params.target_id = opts.targetId;
  if (opts.actorUserId !== undefined) params.actor_user_id = opts.actorUserId;
  const r = await http.get<AuditEvent[]>("/audit", { params });
  return r.data;
}

/** Per-run environment snapshot (versions captured when the pipeline
 *  subprocess started). Used by RunDetailPage's Environment tab and
 *  the LibraryViewer's "computed with" section so catalog references
 *  cite the exact dep versions used. */
export async function getRunEnvironment(runId: string): Promise<RunEnvironment> {
  const r = await http.get<RunEnvironment>(`/runs/${runId}/environment`);
  return r.data;
}

/** Per-run resolved config (defaults + admin overrides). Surfaces what
 *  the pipeline actually consumed so admins don't need to SSH to the
 *  output directory to audit parameters. 404 on pre-feature runs. */
export async function getRunConfig(runId: string): Promise<Record<string, unknown>> {
  const r = await http.get<Record<string, unknown>>(`/runs/${runId}/config`, {
    // It's normal for older runs to not have run_config.yaml; the
    // Environment tab silently shows a fallback rather than toasting.
    skipErrorToast: true,
  } as any);
  return r.data;
}

/** Auth-aware URL for "Download run bundle" -- a zip with the three
 *  reproducibility artifacts (run_config.yaml + environment.yaml +
 *  decisions.jsonl). Used as a direct <a href> link so the browser
 *  saves the file rather than rendering JSON. */
export function runBundleUrl(runId: string): string {
  return withKey(`/api/runs/${encodeURIComponent(runId)}/bundle`);
}
